import { UTM } from '../map/projection';
import { Range as TimeRange } from '../timeline/timeline-common';
import { LineString } from './optimized-marching-squares';
import { LocationTime } from '../data-source/types';
import { BufferedScalarField } from './scalar-field-impl';

export interface CacheKey {
	key: string,
	projectId: string,
	runId: string,
	start: number,
	end: number,
	range: {
		min: number,
		max: number
	}
	version: number,
	available: boolean
}

export interface ScalarMap {
	locationTime: LocationTime,
	scalars: { [key: string]: number }
}

export interface ImpactMap {
	highestGoi: string,
	gois: {
		[key: string]: GoiImpact
	}
}
export interface GoiImpact {
	goiId: string,
	highestBand: number,
	bands: {
		[key: number]: BandImpact
	}
}
export interface BandImpact {
	value: number,
	size: number,
	score: ImpactScore
}
export interface ImpactScore {
	scalar: number,
	size: number,
	combined: number
}

/**
 * This interface converts any content to an array of scalarfields that
 * might be present in the given content.
  */
export interface ScalarFieldProducer {
	getScalarFields: (content: any) => BufferedScalarField[];
}

/**
 * Simple interface that holds an x and a y coordinate
 */
export interface Point {
	x: number,
	y: number
}
/**
 * Simple interface that holds the minimum and the maximum x and y coordinate like e.g. bounds.x.min .
 */
export interface Bounds {
	x: { min: number, max: number },
	y: { min: number, max: number }
}

export interface ValueRange {
	min: number,
	max: number
}

/**
 * The bounds used by the scalar field iterator together with the step 
 * size and number of steps in each direction.
 */
export interface IterationBounds extends Bounds {
	x: { min: number, max: number, step: number, count: number },
	y: { min: number, max: number, step: number, count: number }
}
export interface NativeIterationBounds extends IterationBounds {
	rowOrientation: boolean,
	x: { min: number, max: number, step: number, count: number },
	y: { min: number, max: number, step: number, count: number }
}
export interface ScalarValueProvider {
	/**
	 * Returns the value for the given location and optional instant in time or time range. When a time range is given a reducer
	 * will reduce all values found for given time range
	 * @param x The UTM x coordinate of the location where to get the value(s) for.
	 * @param y The UTM y coordinate of the location where to get the value(s) for.
	 */
	value(x: number, y: number, t?: number): number,
}
/**
 * A type that defines a reducer that is able to reduce a set of values for the same location and quantity over time.
 */
export type ScalarFieldValueReducer = (previousValue: number, value: number, index: number, list: number[]) => number;
//some common value reducers: average, minimum, maximum and add
export const ScalarFieldValueReducers = {
	avg: (previousValue: number, value: number, index: number, list: number[]) => previousValue === undefined ? value / list.length : previousValue + value / list.length,
	min: (previousValue: number, value: number, index: number, list: number[]) => previousValue === undefined ? value : Math.min(previousValue, value),
	max: (previousValue: number, value: number, index: number, list: number[]) => previousValue === undefined ? value : Math.max(previousValue, value),
	add: (previousValue: number, value: number, index: number, list: number[]) => previousValue === undefined ? value : previousValue + value
}

/**
 * The ScalarField interface which defines the methods that every scalar 
 * field should expose.
 */
export interface ScalarField extends ScalarValueProvider {
	/**
	 * The title of this scalar field.
	 */
	getTitle(): string,

	/**
	 * The unit usid in this scalar field
	 */
	getUnit(): string,

	/**
	 * Any additionl properties that might be relevant to the user of this scalar field.
	 */
	getProperties(): any,

	/**
	 * The step size in x and y direction in metres
	 */
	getStepSize(): Point,
	setStepSize(stepSize: Point): void,

	/**
	 * The projection that needs to be used when to other projections  */
	projection(): UTM,

	/**
	 * The UTM bounds of the scalar field
	 */
	utmBounds(): Bounds,

	/**
	 * The time range in which the scalar field is valid. Can be undefined.
	 */
	timeRange(): TimeRange
	
	/**
	 * The time range in which the scalar field is valid. Can be undefined.
	 */
	timeRange(): TimeRange

	/**
	 * Returns the iterator for this scalar field. The iterator returns 
	 * a ScalarFieldPoint which holds location information and the 
	 * value for that location.
	 */
	iterator(topToBottom?: boolean): Iterable<ScalarFieldPoint>,

	/**
	 * Returns the bounds used by the iterator
	 */
	iterationBounds(): IterationBounds,

	/**
	 * Returns all values in a Float32Array running from bottom to top and from left to right
	 */
	getAsTexture(): { width: number, height: number, values: Float32Array },

	//caching related stuff
	getKey(): string,
	setCacheKey(cacheKey: CacheKey): void,
	getCacheKey(): CacheKey
	addSource(key: string): void,
	hasSource(key: string): boolean,
	addSources(sources: Set<string>): void,
	getSources(): Set<string>,
	getIsolines(): { value: number, lines: LineString[][] }[],
	calculateIsoLines(thresholds: number[], sample?: Point): void
}
/**
 * This interface represents a location in a scalar field with its value. */
export interface ScalarFieldPoint {
	/**
	 * The index position in the scalar field. 
	 */
	index: Point,
	/**
	 * The  UTM position in the scalar field.
	 */
	position: Point,
	/**
	 * The instant or time range for which the value is valid
	 */
	time: number | TimeRange,
	/**
	 * The value in the scalar field at given position
	 */
	value: number
}

